import * as React from 'react';

import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import { HOCPanel } from '../../Panel/Panel';
import { VideoPlayList } from '../../VideoPlayList';
import InputSearch from '../../../containers/Learn/Search/InputSearch';
import { LearnNotFound } from '../../../containers/Learn/LearnNotFound/LearnNotFound';
import { localeData } from '../../../locales/data';
import { addModifiers, formatCountWithDuration } from '../../../shared/helpers';

import { CoursesLessonProps, CourseLessonContentProps } from './types';
import './Courses.scss';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import analytics from '../../../shared/services/analytics/analytics';
import { useYouTubePlayerTracking } from '../../../shared/services/analytics/youtubeTracking';
import { useLearnScope } from '../../../containers/Learn/LearnScopeContext';

const Panel = HOCPanel();

interface ReactVideoProps {
  uuid: string;
  currentTime: number;
  handlePlayNextVideo: () => void;
  handleExpandVideo: (e: any) => void;
  isExpanded: boolean;
  showExpandedArrow: boolean;
  source: string;
  name?: string;
  autoPlay?: string;
  modifiers?: string[];
  list?: string;
}

type ReactVideoVidyardProps = Pick<ReactVideoProps, 'uuid' | 'handlePlayNextVideo' | 'autoPlay' | 'modifiers'>;

type ReactVideoYoutubeProps = Pick<ReactVideoProps, 'uuid' | 'name' | 'modifiers' | 'autoPlay' | 'list'>;

export function ReactVideoVidyard({ uuid, handlePlayNextVideo, autoPlay = '1', modifiers = [] }: ReactVideoVidyardProps) {
  const userId = useSelector((state: any) => state.getIn(['user', 'userId']));
  const placeholderEl = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    const win: any = window;
    let player: any = null;

    const renderPlayer = () => {
      win.VidyardV4.api.renderPlayer(placeholderEl.current).then((playerInstance: any) => {
        player = playerInstance;
        player.on('playerComplete', handlePlayNextVideo);
      });
    };

    if (win.VidyardV4) {
      renderPlayer();
    } else {
      document.addEventListener('onVidyardAPI', renderPlayer);
    }

    return () => {
      if (player) {
        win.VidyardV4.api.destroyPlayer(player);
      }
    };
  }, [uuid]);

  return (
    <div key={uuid} className={addModifiers('learn-courses__iframe', modifiers)}>
      <img
        ref={placeholderEl}
        className="vidyard-player-embed"
        src={`//play.vidyard.com/${uuid}.jpg`}
        data-v="4"
        data-uuid={uuid}
        data-type="inline"
        data-autoplay={autoPlay}
        data-vydata={encodeURIComponent(JSON.stringify({ user: userId }))}
      />
    </div>
  );
}

export function ReactVideoYoutube({ uuid, name, modifiers, autoPlay = '1', list }: ReactVideoYoutubeProps) {
  const listUrlPart = list ? `&list=${list}` : '';
  const playerId = `youtube-player-${uuid}`;
  const origin = typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : '';
  const iframeRef = React.useRef<HTMLIFrameElement>(null);

  useYouTubePlayerTracking(iframeRef as React.RefObject<HTMLIFrameElement>, { videoKey: uuid, trackingName: name || uuid });

  return (
    <iframe
      ref={iframeRef}
      id={playerId}
      className={addModifiers('learn-courses__iframe', modifiers)}
      allowFullScreen={true}
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      src={`https://www.youtube-nocookie.com/embed/${uuid}?rel=0${listUrlPart}&autoplay=${autoPlay}&modestbranding=1&enablejsapi=1&origin=${origin}`}
    />
  );
}

function ReactVideo({ uuid, source, name, isExpanded, showExpandedArrow, handleExpandVideo, handlePlayNextVideo }: ReactVideoProps) {
  const isVidyardVideo = source !== undefined && source.toLowerCase() === 'vidyard';
  if (uuid && source) {
    return (
      <div className="learn-courses__iframe-wrapper">
        {isVidyardVideo && <ReactVideoVidyard key={uuid} uuid={uuid} handlePlayNextVideo={handlePlayNextVideo} />}
        {!isVidyardVideo && <ReactVideoYoutube uuid={uuid} name={name} />}
        {showExpandedArrow && (
          <div className="learn-courses__expand" onClick={handleExpandVideo}>
            <i className={addModifiers('learn-courses__expand-icon', isExpanded ? ['left'] : ['right'])} />
          </div>
        )}
      </div>
    );
  } else {
    return null;
  }
}

const CourseLessonContent = ({
  currentVideo,
  course,
  isExpanded,
  id,
  currentTime,
  handlePlayNextVideo,
  handleExpandVideo,
  path,
  fullPlaylist,
}: CourseLessonContentProps) => {
  const isSimulate = useLearnScope() === 'simulate';
  const intl = useIntl();
  const videosLabel = intl.formatMessage({ id: 'coursesDetails.videos', defaultMessage: localeData.en['coursesDetails.videos'] });
  return (
    <Panel title={course.title} panelModifiers={['learn']}>
      <div className={addModifiers('learn-courses__row', fullPlaylist ? ['vertical-content'] : [])}>
        <div className={addModifiers('learn-courses__col', isExpanded ? ['expand'] : [])}>
          <ReactVideo
            key={id}
            uuid={course.videos.find((item: any) => item.id === id).uid}
            currentTime={currentTime}
            handlePlayNextVideo={handlePlayNextVideo}
            handleExpandVideo={handleExpandVideo}
            isExpanded={isExpanded}
            showExpandedArrow={!fullPlaylist}
            source={course.videos.find((item: any) => item.id === id).source}
            name={course.videos.find((item: any) => item.id === id).title}
          />
        </div>
        {!isExpanded && (
          <div className="learn-courses__col learn-courses__col--small">
            <VideoPlayList
              title={formatCountWithDuration(`${course.videos.length} ${videosLabel}`, course.duration)}
              items={course}
              current={course.videos.findIndex((item: any) => item.id === id)}
              path={path}
              onItemClick={(videoId: string) =>
                analytics.trackCustomEvent(isSimulate ? 'sim_course_menu_view' : 'learn_course_menu_view', { event_label: videoId })
              }
            />
          </div>
        )}
      </div>
      <div className="learn-courses__row learn-courses__row--small-space">
        <div className="learn-courses__col">
          <div className="learn-courses__between">
            <h3 className="learn-courses__title">
              <FormattedMessageHelper tagName="span" id="courseLesson.1" defaultMessage={localeData.en['courseLesson.1']} />
            </h3>
          </div>
          <div className="learn-courses__text">
            {currentVideo ? (
              currentVideo.description
            ) : (
              <FormattedMessageHelper
                tagName="span"
                id="courseLesson.no-description"
                defaultMessage={localeData.en['courseLesson.no-description']}
              />
            )}
          </div>
        </div>
        <div className="learn-courses__col learn-courses__col--small" />
      </div>
    </Panel>
  );
};

export const CourseLesson = ({
  course,
  fetchingData,
  id,
  handlePlayNextVideo,
  currentTime,
  isExpanded,
  handleExpandVideo,
  path,
  fullPlaylist,
}: CoursesLessonProps) => {
  const currentVideo = course ? course.videos.find((item: any) => item.id === id) : null;

  return (
    <div className="learn__content">
      {fetchingData || course === null ? (
        <div className="loader">
          <Loader active={true} inline="centered" size="massive" />
        </div>
      ) : currentVideo ? (
        <div>
          <div className="learn-search-bar">
            <InputSearch modifiers={['medium']} hasCheckboxFilter={true} filterType="videoCourse" path={path} />
          </div>
          <CourseLessonContent
            course={course}
            id={id}
            handlePlayNextVideo={handlePlayNextVideo}
            currentTime={currentTime}
            isExpanded={isExpanded}
            handleExpandVideo={handleExpandVideo}
            currentVideo={currentVideo}
            path={path}
            fullPlaylist={fullPlaylist}
          />
        </div>
      ) : (
        <LearnNotFound />
      )}
    </div>
  );
};
